// packages/ui-kit/src/brand/TaskRow.tsx
// C3F v18 — reference: design/system/components/brand/TaskRow.{jsx,prompt.md}
// The product's list row: status dot + title + meta + trailing action. Lists
// in Praesto are rows and cards — there is no Table, on purpose.
import { type CSSProperties, type ReactNode } from "react";

type TaskState = "success" | "warning" | "danger" | "info" | "accent";
type TaskTone = "default" | "night" | "highlight";

interface Props {
  title: ReactNode;
  meta?: ReactNode;
  /** The 10px solid status dot is the product's status vocabulary. */
  state?: TaskState;
  action?: ReactNode;
  onClick?: () => void;
  /** "night" for rows inside always-Night surfaces; "highlight" for the single
   *  emphasized row (paper card on night). */
  tone?: TaskTone;
  done?: boolean;
  className?: string;
  style?: CSSProperties;
}

const DOT: Record<TaskState, string> = {
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-danger",
  info: "bg-info",
  accent: "bg-accent",
};

export function TaskRow({
  title,
  meta,
  state = "accent",
  action,
  onClick,
  tone = "default",
  done = false,
  className,
  style,
}: Props) {
  const night = tone === "night";
  const highlight = tone === "highlight";
  const toneCls = highlight
    ? "bg-praesto-paper text-praesto-night border border-transparent"
    : night
      ? "bg-[rgba(244,240,233,.055)] text-praesto-paper border border-transparent"
      : "bg-elevated text-fg-default border border-border-default";
  // Hover speaks through the BACKGROUND, never the border: darkening the
  // outline was the one case in the system and it read as a stray black box.
  const hoverCls = onClick && !night && !highlight ? "cursor-pointer hover:bg-subtle" : "";
  const mutedCls = night ? "text-sidebar-muted" : "text-fg-muted";
  return (
    <div
      onClick={onClick}
      className={[
        "flex min-h-[60px] items-center gap-3.5 rounded-md px-4 py-3 transition-colors duration-fast",
        toneCls,
        hoverCls,
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      style={style}
    >
      <span
        aria-hidden
        className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${DOT[state]} ${done ? "opacity-40" : ""}`}
      />
      <div className="min-w-0 flex-1">
        <div
          className={`truncate text-sm font-semibold ${
            // Strikethrough already says the task is closed. Dimmed COLOR (not
            // opacity: opacity never shows up in token measurements) keeps the
            // record readable — knowing what got done is what the row is for.
            done ? `line-through ${mutedCls}` : ""
          }`}
        >
          {title}
        </div>
        {meta !== undefined && <div className={`mt-0.5 text-caption ${mutedCls}`}>{meta}</div>}
      </div>
      {action !== undefined && <div className="flex-shrink-0">{action}</div>}
    </div>
  );
}
